import { Injectable } from '@angular/core';
import {
  endDateValidatorFactory,
  FormSection,
  hiddenIdField,
  requiredValidator,
} from 'src/app/shared/components/dynamic-form/form-sections.model';
import { TranslationService } from 'src/app/shared/services/translation.service';
import {
  ACTIVATIONCONFIG,
  ApiMethod,
  Endpoints,
  SCREENMODE,
} from '../utils/constants';
import { map, Observable, of, Subject, takeUntil } from 'rxjs';
import { HttpService } from '../http/http.service';
import { LodashService } from '../lodash/lodash.service';
import { AuthService } from '../auth/auth.service';

@Injectable({
  providedIn: 'root',
})
export class ActivationMgmtService {
  staticText: any = TranslationService.staticTextData;
  screenMode = SCREENMODE;
  activateOptions: any = [
    {
      id: ACTIVATIONCONFIG.entire,
      name: 'entire',
      display_name: 'Entire',
    },
    {
      id: ACTIVATIONCONFIG.range,
      name: 'range',
      display_name: 'Range',
    },
  ];

  importProductDate: any = {
    type: 'date',
    label: `Imported Product Date`,
    name: 'imported_date',
    labelReq: false,
    required: true,
    validators: [],
    meta: {
      onchangeCallback: () => { },
    },
  };

  activationConfig: FormSection[] = [];

  reconiliationConfig: FormSection[] = [
    {
      title: this.staticText?.reconcilication?.view_detail_title,
      key: 'reconciliation',
      sectionType: 'single',
      columns: 4, // Number of columns per row
      meta: { isEmptyCheck: true },
      fields: [
        {
          type: 'text',
          label: this.staticText?.reconcilication?.view_detail_field2,
          name: 'company',
        },
        {
          type: 'text',
          label: this.staticText?.reconcilication?.view_detail_field1,
          name: 'issue_level_name',
        },
        {
          type: 'text',
          label: this.staticText?.reconcilication?.view_detail_field10,
          name: 'issue_type',
        },
        {
          type: 'date',
          label: this.staticText?.reconcilication?.view_detail_field3,
          name: 'created_on',
        },
        {
          type: 'text',
          label: this.staticText?.reconcilication?.view_detail_field4,
          name: 'created_by',
        },
        {
          type: 'text',
          label: this.staticText?.reconcilication?.reported_uid,
          name: 'serial_number',
        },
         {
          type: 'text',
          label: this.staticText?.reconcilication?.start_serial_number,
          name: 'start_serial_number',
        },
         {
          type: 'text',
          label: this.staticText?.reconcilication?.end_serial_number,
          name: 'end_serial_number',
        },
         {
          type: 'text',
          label: this.staticText?.reconcilication?.reported_uid_list,
          name: 'serial_number_list',
          meta:{
            viewAction: true,
              onchangeCallback: () => { },
          }
        },
        {
          type: 'text',
          label: this.staticText?.reconcilication?.view_detail_field5,
          name: 'product_name',
        },
        {
          type: 'text',
          label: this.staticText?.reconcilication?.view_detail_field7,
          name: 'uom',
        },
        {
          type: 'text',
          label: this.staticText?.reconcilication?.view_detail_field8,
          name: 'mrp',
          meta: {
            isCurrency: true,
            inputSuffixText: '$',
          },
        },
        {
          type: 'text',
          label: this.staticText?.reconcilication?.view_detail_field12,
          name: 'issued_uid_count',
          meta: { viewConfig: { colspan: 2 } },
        },
        {
          type: 'text',
          label: this.staticText?.reconcilication?.view_detail_field9,
          name: 'description',
          meta: { viewConfig: { colspan: 4 } },
        },
      ],
    },
  ];

  private readonly destroy$ = new Subject<void>();
  isSCP: boolean = false;

  constructor(
    private readonly _http: HttpService,
    private readonly _lodash: LodashService,
    private readonly _auth: AuthService
  ) {
    this.isSCP = this._auth?.isSCPRole;
    this.activationConfig = [
      {
        title: '',
        key: 'site_details',
        sectionType: 'single',
        columns: 3, // Number of columns per row
        meta: { isEmptyCheck: true, hideSectionTitle: true },
        fields: [
          hiddenIdField,
          // Only add company field if NOT Company
          ...(!this.isSCP
            ? [
                {
                  type: 'select',
                  label: this.staticText?.common?.company_name ?? 'Company Name',
                  name: 'company',
                  required: true,
                  validators: [requiredValidator],
                  options: [],
                  apiEndpoint: Endpoints.GET_DROPDOWN_COMPANY,
                },
              ]
            : []),
          {
            type: 'select',
            label:
              this.staticText?.activation_mgmt?.add_mgmt?.activation_field1,
            name: 'site_id',
            required: true,
            validators: [requiredValidator],
            ...(this.isSCP
              ? {
                apiEndpoint: Endpoints.GET_SITE_DROPDOWN,
              }
              : {
                dependsOn: 'company',
                apiEndpoint: Endpoints.ONBOARD_LOGINUSER_SITE_DROPDOWN,
              }),
            meta: {
              apiLoader: true,
            },
          },
          {
            type: 'select',
            label:
              this.staticText?.activation_mgmt?.add_mgmt?.activation_field2,
            name: 'order_id',
            required: true,
            validators: [requiredValidator],
            options: [],
            apiEndpoint: Endpoints.GET_ORDER_LIST,
            dependsOn: 'site_id',
            meta: {
              apiLoader: true,
              viewAction: true,
              onchangeCallback: () => { },
              help: {
                template: true,
                textFormatter: (value: any, group: any) => {
                  if (value)
                    return `<span class='blue-highlight-with-underline'>View Order Detail</span>`;
                  else return ``;
                },
                cb: (field: any, group: any) => { },
              },
            },
          },
          {
            type: 'select',
            label:
              this.staticText?.activation_mgmt?.add_mgmt?.activation_field3,
            name: 'product',
            required: true,
            validators: [requiredValidator],
            apiEndpoint: Endpoints.GET_ORDER_PRODUCT_LIST,
            options: [],
            dependsOn: 'order_id',
            meta: {
              viewAction: true,
              onchangeCallback: () => { },
              help: {
                template: true,
                textFormatter: (value: any, group: any) => {
                  if (value) {
                    return `<span class='blue-highlight-with-underline'>View Product Detail</span>`;
                  } else return ``;
                },
                cb: (field: any, group: any) => { },
              },
            },
          },
          {
            type: 'select',
            label: this.staticText?.activation?.Package_details,
            name: 'package',
            required: true,
            validators: [requiredValidator],
            options: [],
            meta: {},
          },
        ],
      },
      {
        title: this.staticText?.activation_mgmt?.add_mgmt?.activation_title,
        key: 'activation_details',
        sectionType: 'single',
        columns: 3, // Number of columns per row
        meta: { isEmptyCheck: true },
        fields: [
          hiddenIdField,

          {
            type: 'date',
            label:
              this.staticText?.activation_mgmt?.add_mgmt?.activation_field5,
            name: 'production_date',
            labelReq: false,
            required: true,
            validators: [requiredValidator],
            meta: {
              onchangeCallback: () => { },
              min_date: new Date(),
            },
          },
          {
            type: 'date',
            label:
              this.staticText?.activation_mgmt?.add_mgmt?.activation_field6,
            name: 'expiry_date',
            labelReq: false,
            required: true,
            validators: [
              requiredValidator,
              {
                name: 'min',
                validator: endDateValidatorFactory('start_date'),
                message: `Please select future dates from the production date`,
              },
            ],
            meta: {
              onchangeCallback: () => { },
            },
          },
          {
            type: 'text',
            label:
              this.staticText?.activation_mgmt?.add_mgmt?.activation_field7,
            name: 'lot_number',
            labelReq: false,
            required: true,
            validators: [requiredValidator],
            meta: {
              onchangeCallback: () => { },
            },
          },
        ],
      },
    ];
  }

  getActivationConfig(mode: any, apiResponse:any = null): any {
    const config = this._lodash.Clonedata(this.activationConfig);
    if (mode == this.screenMode.ADD) {
      const templateDotBorder = {
        type: 'template', // New template type field
        label: 'Dotted Border',
        name: 'top_padding',
        templateRef: ``,
        meta: { table_display: false },
        context: {},
      };
      const templatePadding = {
        type: 'template', // New template type field
        label: 'Dotted Border',
        name: 'dashed_border',
        templateRef: ``,
        meta: { table_display: false },
        context: {},
      };
      config[0].fields.unshift(templateDotBorder);
      config[0].fields.push(templatePadding);
    }
    if (apiResponse?.data?.is_bulk_mobile) 
      config?.splice(1,1)
    return config;
  }

  private buildEndpoint(base: string, param?: any): string {

    return param ? `${base}${param}` : base;
  }

  private makeRequest(endpoint: any, method: ApiMethod): Observable<any> {
    return this._http.requestCall(endpoint, method).pipe(
      map((e: any) => e),
      takeUntil(this.destroy$)
    );
  }

  updateStampBulkStatus(formData: any) {
    let endpoint: any = `${Endpoints.ACTIVATION_STAMP_BULK_ACTIVATION_UPDATE}`;
    return this._http.requestCall(endpoint, ApiMethod.PUT, formData, {
      observe: 'response',
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  }

  updateBulkStatus(formData: any) {
    let endpoint: any = `${Endpoints.ACTIVATION_BULK_ACTIVATION_UPDATE}`;
    return this._http.requestCall(endpoint, ApiMethod.PUT, formData, {
      observe: 'response',
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  }
  updateCaseBulkStatus(formData: any) {
    let endpoint: any = `${Endpoints.CASE_DETAIL_BULK_UPLOAD}`;
    return this._http.requestCall(endpoint, ApiMethod.PUT, formData, {
      observe: 'response',
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  }
  updateProductBulkStatus(formData: any) {
    let endpoint: any = `${Endpoints.PRODUCT_DETAIL_BULK_UPLOAD}`;
    return this._http.requestCall(endpoint, ApiMethod.PUT, formData, {
      observe: 'response',
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  }

  saveActivation(formData: any): Observable<any> {
    let endpoint: any = `${Endpoints.GET_ACTIVATION_WEB}`;
    return this._http.requestCall(endpoint, ApiMethod.POST, formData).pipe(
      map((e: any) => e),
      takeUntil(this.destroy$)
    );
  }

  getActivationById(id: any): Observable<any> {
    const endpoint: any = `${Endpoints.GET_ACTIVATION_WEB}${id}/`;
    return this._http.requestCall(endpoint, ApiMethod.GET).pipe(
      map((e: any) => e),
      takeUntil(this.destroy$)
    );
  }


  getActivationByIdPage(
    id: any,
    page: number,
    pageSize: number = 20
  ): Observable<any> {


    const offset = (page - 1) * pageSize;


    const end = offset + pageSize;

    const endpoint: any = `${Endpoints.GET_ACTIVATION_WEB_STAMP}?activation_id=${id}&offset=${offset}&end=${end}`;

    return this._http.requestCall(endpoint, ApiMethod.GET).pipe(
      map((res: any) => res || {}),
      takeUntil(this.destroy$)
    );
  }


  getActivationList(param: any): Observable<any> {
    const endpoint = this.buildEndpoint(Endpoints.GET_ACTIVATION_LIST, param);
    return this.makeRequest(endpoint, ApiMethod.GET);
  }

  getActivationNextStatus(id: any): Observable<any> {
    const endpoint = this.buildEndpoint(
      `${Endpoints.GET_ACTIVATION_NEXT_STATUS}${id}/`
    );
    return this.makeRequest(endpoint, ApiMethod.GET);
  }

  getPackageByProductId(id: any): Observable<any> {
    const endpoint: any = `${Endpoints.GET_PRODUCT_PACKAGE}${id}/`;
    return this._http.requestCall(endpoint, ApiMethod.GET).pipe(
      map((e: any) => e),
      takeUntil(this.destroy$)
    );
  }

  getReelReemDetails(order_id: any, product_id: any): Observable<any> {
    const endpoint: any = `${Endpoints.GET_GET_ORDER_PRODUCT_MATERIAL_TYPE}?order_id=${order_id}&product_id=${product_id}`;
    return this._http.requestCall(endpoint, ApiMethod.GET).pipe(
      map((e: any) => e),
      takeUntil(this.destroy$)
    );
  }

  getCaseDetailList(activationStampId: any, param: any) {
    const endpoint: any = param
      ? `${Endpoints.ACTIVATION_CASE_DETAIL}${param.includes('?') ? param : `?${param}`}&activation_stamp=${activationStampId}` 
      : `${Endpoints.ACTIVATION_CASE_DETAIL}?activation_stamp=${activationStampId}`;
    return this._http.requestCall(endpoint, ApiMethod.GET).pipe(
      map((e: any) => e),
      takeUntil(this.destroy$)
    );
  }
  getProductDetailList(activationStampId: any, stampId: any, param: any) {
    const endpoint: any = param
      ? `${Endpoints.GET_PRODUCT_DETAIL}${param.includes('?') ? param : `?${param}`}&activation_package=${activationStampId}&activation_stamp=${stampId}`
      : `${Endpoints.GET_PRODUCT_DETAIL}?activation_package=${activationStampId}&activation_stamp=${stampId}`;
    return this._http.requestCall(endpoint, ApiMethod.GET).pipe(
      map((e: any) => e),
      takeUntil(this.destroy$)
    );
  }

  updateStatus(id: any, action: any, formData: any) {
    let endpoint: any = `${Endpoints.UPLOAD_ACTIVATION_STATUS}${id}/?status_key=${action}`;
    return this._http.requestCall(endpoint, ApiMethod.PUT, formData, {
      observe: 'response',
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  }
}
